import React, { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router-dom";
import { fetchUserTokensAPI } from "../services/authService";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const AccessTokens = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [searchText, setSearchText] = useState("");

  const totalCount = rowData?.length ?? 0;

  const columnDefs = [
    { field: "id", headerName: "Id", valueGetter: "node.rowIndex+1", width: 100 },
    {
      field: "accessToken",
      headerName: "Access Token",
      flex: 1.2,
      minWidth: 280,
      cellRenderer: (p) => {
        const v = p.value || "-";
        return (
          <span
            title={v}
            style={{ wordBreak: "break-all", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            {v}
          </span>
        );
      },
    },
    {
      field: "code",
      headerName: "Code",
      flex: 1,
      minWidth: 220,
      cellRenderer: (p) => {
        const v = p.value || "-";
        return (
          <span
            title={v}
            style={{ wordBreak: "break-all", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          >
            {v}
          </span>
        );
      },
    },
    {
      field: "expiresAt",
      headerName: "Expires At",
      width: 190,
      valueFormatter: (p) => {
        const v = p.value;
        if (!v) return "-";
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.toLocaleString();
      },
    },
    { field: "source", headerName: "Source", width: 140 },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 120,
  };

  useEffect(() => {
    const isAuth = JSON.parse(localStorage.getItem("access_token"));
    if (!isAuth) {
      navigate("/");
      return;
    }
    getData();
  }, [navigate]);

  const getData = async () => {
    try {
      let result = await fetchUserTokensAPI("access_token");
      result = await result.json();
      setRowData(result?.data || []);
    } catch (e) {
      console.error("Failed to fetch access tokens:", e);
      setRowData([]);
    }
  };

  // search (AG Grid quick filter)
  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    gridRef.current?.api?.setQuickFilter(val);
  };

  const clearSearch = () => {
    setSearchText("");
    gridRef.current?.api?.setQuickFilter("");
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-col items-start justify-between pb-4 space-y-3 lg:items-center lg:space-y-0 lg:flex-row">
        <h1 className="text-2xl font-semibold">Access Tokens</h1>
        <div className="text-sm text-gray-600">
          Total Tokens: <span className="font-semibold text-gray-900">{totalCount}</span>
        </div>
      </div>

      {/* Search bar (right aligned) */}
      <div className="flex items-center justify-end mb-2">
        <div className="relative w-full max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>
          <input
            type="text"
            value={searchText}
            onChange={onSearchChange}
            placeholder="Search tokens (token, code, source)…"
            className="w-full rounded-md border border-gray-300 pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {searchText ? (
            <button
              type="button"
              title="Clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={clearSearch}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>

      {/* Grid */}
      <div className="ag-theme-alpine overflow-hidden" style={{ height: 500, marginTop: 8 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination
          paginationPageSize={10}
          suppressDragLeaveHidesColumns
        />
      </div>
    </div>
  );
};

export default AccessTokens;
